import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  Mail,
  MessageSquare,
  Bell,
  Send,
  FileText,
  Users,
  BarChart3,
  Plus,
  Eye,
  Edit,
  Trash2,
  ChevronRight,
  Sparkles,
  Globe,
  Search,
  Filter,
  Clock,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Copy,
  History,
  User,
  Building,
  DollarSign,
  Gavel,
  Palette,
  CalendarDays,
  Languages,
  TrendingUp,
  Activity,
  MoreVertical,
  X,
  Check,
  Loader2,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const CommunicationsHubPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [owners, setOwners] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState("compose");
  const [recentMessages, setRecentMessages] = useState([]);
  const [inAppMessages, setInAppMessages] = useState([]);
  
  // Compose state
  const [composeData, setComposeData] = useState({
    channel: "email",
    template_id: "",
    recipients: [],
    subject: "",
    body: "",
    category: "community",
    variables: {}
  });
  const [sending, setSending] = useState(false);
  
  // Template form state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    category: "community",
    channel: "email",
    subject: "",
    body: "",
    variables: [],
    is_active: true
  });
  
  // AI Draft state
  const [aiDraftData, setAiDraftData] = useState({
    context: "",
    message_type: "announcement",
    recipient_name: "",
    tone: "professional"
  });
  const [aiDraft, setAiDraft] = useState("");
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Translate state
  const [showTranslateDialog, setShowTranslateDialog] = useState(false);
  const [translateText, setTranslateText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("Spanish");
  const [translating, setTranslating] = useState(false);
  
  // Filter state
  const [templateFilter, setTemplateFilter] = useState({ category: "", channel: "" });

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, analyticsRes, ownersRes, inAppRes] = await Promise.all([
        axios.get(`${API}/communications/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/communications/analytics?days=30`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/owners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/communications/in-app`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { messages: [] } }))
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setAnalytics(analyticsRes.data);
      setOwners(ownersRes.data || []);
      setInAppMessages(inAppRes.data.messages || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load communications data");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (composeData.recipients.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    if (!composeData.body) {
      toast.error("Please enter a message body");
      return;
    }
    
    try {
      setSending(true);
      const response = await axios.post(
        `${API}/communications/send`,
        composeData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Message sent to ${response.data.recipients_count} recipient(s)`);
      setComposeData({
        channel: "email",
        template_id: "",
        recipients: [],
        subject: "",
        body: "",
        category: "community",
        variables: {}
      });
      setSelectedTemplate(null);
      fetchData();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleAIDraft = async () => {
    if (!aiDraftData.context) {
      toast.error("Please provide context for the AI draft");
      return;
    }
    
    try {
      setAiLoading(true);
      const response = await axios.post(
        `${API}/communications/ai/draft`,
        aiDraftData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiDraft(response.data.draft);
      toast.success(response.data.ai_generated ? "AI draft generated" : "Template draft generated");
    } catch (error) {
      console.error("Error generating AI draft:", error);
      toast.error("Failed to generate draft");
    } finally {
      setAiLoading(false);
    }
  };

  const handleUseAIDraft = () => {
    setComposeData({ ...composeData, body: aiDraft });
    setShowAiDialog(false);
    setAiDraft("");
    setAiDraftData({ context: "", message_type: "announcement", recipient_name: "", tone: "professional" });
    toast.success("Draft added to compose");
  };

  const handleTranslate = async () => {
    if (!translateText) {
      toast.error("Please enter text to translate");
      return;
    }
    
    try {
      setTranslating(true);
      const response = await axios.post(
        `${API}/communications/ai/translate`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { text: translateText, target_language: targetLanguage }
        }
      );
      setTranslatedText(response.data.translated);
      if (response.data.error) {
        toast.error("Translation service unavailable");
      } else {
        toast.success(`Translated to ${targetLanguage}`);
      }
    } catch (error) {
      console.error("Error translating:", error);
      toast.error("Failed to translate");
    } finally {
      setTranslating(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.template_id === templateId);
    if (template) {
      setComposeData({
        ...composeData,
        template_id: templateId,
        channel: template.channel,
        subject: template.subject || "",
        body: template.body,
        category: template.category
      });
      setSelectedTemplate(template);
      toast.success(`Template "${template.name}" loaded`);
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.name || !templateForm.body) {
      toast.error("Please fill in template name and body");
      return;
    }
    
    try {
      if (editingTemplate) {
        await axios.put(
          `${API}/communications/templates/${editingTemplate.template_id}`,
          templateForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Template updated");
      } else {
        await axios.post(
          `${API}/communications/templates`,
          templateForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Template created");
      }
      setShowTemplateForm(false);
      setEditingTemplate(null);
      setTemplateForm({ name: "", category: "community", channel: "email", subject: "", body: "", variables: [], is_active: true });
      fetchData();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm("Delete this template?")) return;
    
    try {
      await axios.delete(
        `${API}/communications/templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(error.response?.data?.detail || "Failed to delete template");
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      category: template.category,
      channel: template.channel,
      subject: template.subject || "",
      body: template.body,
      variables: template.variables || [],
      is_active: template.is_active
    });
    setShowTemplateForm(true);
  };

  const handleSelectAllOwners = () => {
    const allOwnerIds = owners.map(o => o.owner_id);
    setComposeData({ ...composeData, recipients: allOwnerIds });
    toast.success(`Selected all ${allOwnerIds.length} owners`);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      financial: <DollarSign className="w-4 h-4" />,
      compliance: <Gavel className="w-4 h-4" />,
      drc: <Palette className="w-4 h-4" />,
      community: <Users className="w-4 h-4" />,
      lifecycle: <Clock className="w-4 h-4" />,
      emergency: <AlertTriangle className="w-4 h-4" />,
      system: <Bell className="w-4 h-4" />
    };
    return icons[category] || <Mail className="w-4 h-4" />;
  };

  const getChannelIcon = (channel) => {
    const icons = {
      email: <Mail className="w-4 h-4" />,
      sms: <MessageSquare className="w-4 h-4" />,
      in_app: <Bell className="w-4 h-4" />
    };
    return icons[channel] || <Mail className="w-4 h-4" />;
  };

  const getChannelBadge = (channel) => {
    const config = {
      email: { color: "bg-blue-100 text-blue-800", label: "Email" },
      sms: { color: "bg-green-100 text-green-800", label: "SMS" },
      in_app: { color: "bg-purple-100 text-purple-800", label: "In-App" }
    };
    const c = config[channel] || { color: "bg-gray-100", label: channel };
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const config = {
      financial: { color: "bg-emerald-100 text-emerald-800" },
      compliance: { color: "bg-red-100 text-red-800" },
      drc: { color: "bg-purple-100 text-purple-800" },
      community: { color: "bg-blue-100 text-blue-800" },
      lifecycle: { color: "bg-orange-100 text-orange-800" },
      emergency: { color: "bg-red-100 text-red-800" },
      system: { color: "bg-gray-100 text-gray-800" }
    };
    const c = config[category] || { color: "bg-gray-100" };
    return <Badge className={c.color}>{category}</Badge>;
  };

  const filteredTemplates = templates.filter(t => {
    if (templateFilter.category && t.category !== templateFilter.category) return false;
    if (templateFilter.channel && t.channel !== templateFilter.channel) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Mail className="w-12 h-12 text-primary mx-auto animate-pulse" />
          <p className="mt-4 text-muted-foreground">Loading Communications Hub...</p>
        </div>
      </div>
    );
  }

  const pageContent = (
    <div className="p-6 lg:p-8" data-testid="communications-hub-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h1 className="font-serif text-xl font-semibold">Communications Hub</h1>
            <p className="text-xs text-muted-foreground">Unified messaging across all channels</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Dialog open={showTranslateDialog} onOpenChange={setShowTranslateDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Languages className="w-4 h-4 mr-2" />
                Translate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>AI Translation</DialogTitle>
                <DialogDescription>Translate text to another language</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Text to Translate</Label>
                  <Textarea 
                    value={translateText}
                    onChange={(e) => setTranslateText(e.target.value)}
                    placeholder="Enter text..."
                    rows={4}
                  />
                </div>
                <div>
                  <Label>Target Language</Label>
                  <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Spanish">Spanish</SelectItem>
                      <SelectItem value="French">French</SelectItem>
                      <SelectItem value="German">German</SelectItem>
                      <SelectItem value="Chinese">Chinese</SelectItem>
                      <SelectItem value="Japanese">Japanese</SelectItem>
                      <SelectItem value="Korean">Korean</SelectItem>
                      <SelectItem value="Portuguese">Portuguese</SelectItem>
                    </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleTranslate} disabled={translating} className="w-full">
                    {translating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Languages className="w-4 h-4 mr-2" />}
                    Translate
                  </Button>
                  {translatedText && (
                    <div className="space-y-2">
                      <Label>Translation ({targetLanguage})</Label>
                      <div className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap">
                        {translatedText}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => {
                        navigator.clipboard.writeText(translatedText);
                        toast.success("Copied to clipboard");
                      }}>
                        <Copy className="w-4 h-4 mr-2" /> Copy
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="ai-draft-btn">
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Draft
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>AI Message Drafting</DialogTitle>
                  <DialogDescription>Let AI help you draft your message</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Context *</Label>
                    <Textarea 
                      value={aiDraftData.context}
                      onChange={(e) => setAiDraftData({...aiDraftData, context: e.target.value})}
                      placeholder="Describe what you want to communicate..."
                      rows={4}
                      data-testid="ai-context-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Message Type</Label>
                      <Select value={aiDraftData.message_type} onValueChange={(v) => setAiDraftData({...aiDraftData, message_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="announcement">Announcement</SelectItem>
                          <SelectItem value="reminder">Reminder</SelectItem>
                          <SelectItem value="notice">Notice</SelectItem>
                          <SelectItem value="welcome">Welcome</SelectItem>
                          <SelectItem value="update">Update</SelectItem>
                          <SelectItem value="invitation">Invitation</SelectItem>
                          <SelectItem value="thank_you">Thank You</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tone</Label>
                      <Select value={aiDraftData.tone} onValueChange={(v) => setAiDraftData({...aiDraftData, tone: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="friendly">Friendly</SelectItem>
                          <SelectItem value="formal">Formal</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Recipient Name (optional)</Label>
                    <Input 
                      value={aiDraftData.recipient_name}
                      onChange={(e) => setAiDraftData({...aiDraftData, recipient_name: e.target.value})}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                  <Button className="w-full" onClick={handleAIDraft} disabled={aiLoading} data-testid="generate-draft-btn">
                    {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate Draft
                  </Button>
                  {aiDraft && (
                    <div className="space-y-2">
                      <Label>Generated Draft</Label>
                      <ScrollArea className="h-[200px] border rounded-lg p-3">
                        <div className="text-sm whitespace-pre-wrap">{aiDraft}</div>
                      </ScrollArea>
                      <Button onClick={handleUseAIDraft} className="w-full" data-testid="use-draft-btn">
                        <Check className="w-4 h-4 mr-2" /> Use This Draft
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            <Button variant="outline" size="icon" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

      {/* Main Content */}
      <div className="mt-6">
        {/* Analytics Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.total_messages || 0}</p>
                  <p className="text-xs text-muted-foreground">Messages (30d)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.delivery_rate?.toFixed(0) || 0}%</p>
                  <p className="text-xs text-muted-foreground">Delivery Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{templates.length}</p>
                  <p className="text-xs text-muted-foreground">Templates</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{owners.length}</p>
                  <p className="text-xs text-muted-foreground">Recipients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Activity className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.by_status?.queued || 0}</p>
                  <p className="text-xs text-muted-foreground">Queued</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="compose" data-testid="compose-tab">
              <Send className="w-4 h-4 mr-2" /> Compose
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">
              <FileText className="w-4 h-4 mr-2" /> Templates
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">
              <BarChart3 className="w-4 h-4 mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="in-app" data-testid="in-app-tab">
              <Bell className="w-4 h-4 mr-2" /> In-App Messages
            </TabsTrigger>
          </TabsList>

          {/* Compose Tab */}
          <TabsContent value="compose">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Compose Message</CardTitle>
                    <CardDescription>Send emails, SMS, or in-app notifications</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Channel</Label>
                        <Select value={composeData.channel} onValueChange={(v) => setComposeData({...composeData, channel: v})}>
                          <SelectTrigger data-testid="channel-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4" /> Email
                              </div>
                            </SelectItem>
                            <SelectItem value="sms">
                              <div className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" /> SMS
                              </div>
                            </SelectItem>
                            <SelectItem value="in_app">
                              <div className="flex items-center gap-2">
                                <Bell className="w-4 h-4" /> In-App
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={composeData.category} onValueChange={(v) => setComposeData({...composeData, category: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="financial">Financial</SelectItem>
                            <SelectItem value="compliance">Compliance</SelectItem>
                            <SelectItem value="drc">DRC</SelectItem>
                            <SelectItem value="community">Community</SelectItem>
                            <SelectItem value="lifecycle">Lifecycle</SelectItem>
                            <SelectItem value="emergency">Emergency</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Recipients ({composeData.recipients.length} selected)</Label>
                        <Button variant="ghost" size="sm" onClick={handleSelectAllOwners}>
                          Select All
                        </Button>
                      </div>
                      <Select onValueChange={(v) => {
                        if (!composeData.recipients.includes(v)) {
                          setComposeData({...composeData, recipients: [...composeData.recipients, v]});
                        }
                      }}>
                        <SelectTrigger data-testid="recipients-select">
                          <SelectValue placeholder="Add recipients..." />
                        </SelectTrigger>
                        <SelectContent>
                          {owners.map(owner => (
                            <SelectItem key={owner.owner_id} value={owner.owner_id}>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                {owner.name} ({owner.email})
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {composeData.recipients.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {composeData.recipients.map(rid => {
                            const owner = owners.find(o => o.owner_id === rid);
                            return (
                              <Badge key={rid} variant="secondary" className="cursor-pointer hover:bg-destructive/20" onClick={() => {
                                setComposeData({...composeData, recipients: composeData.recipients.filter(r => r !== rid)});
                              }}>
                                {owner?.name || rid} <X className="w-3 h-3 ml-1" />
                              </Badge>
                            );
                          })}
                          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setComposeData({...composeData, recipients: []})}>
                            Clear All
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {composeData.channel === "email" && (
                      <div>
                        <Label>Subject</Label>
                        <Input 
                          value={composeData.subject}
                          onChange={(e) => setComposeData({...composeData, subject: e.target.value})}
                          placeholder="Enter subject line..."
                          data-testid="subject-input"
                        />
                      </div>
                    )}
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Message</Label>
                        {selectedTemplate && (
                          <Badge variant="outline">
                            Template: {selectedTemplate.name}
                            <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setSelectedTemplate(null)} />
                          </Badge>
                        )}
                      </div>
                      <Textarea 
                        value={composeData.body}
                        onChange={(e) => setComposeData({...composeData, body: e.target.value})}
                        placeholder="Enter your message..."
                        rows={10}
                        data-testid="message-body-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Use {'{variable_name}'} for personalization. Available: owner_name, property_address, amount, due_date
                      </p>
                    </div>
                    
                    <Button className="w-full" onClick={handleSendMessage} disabled={sending} data-testid="send-message-btn">
                      {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Message
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Quick Templates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-2">
                        {templates.slice(0, 12).map(template => (
                          <div 
                            key={template.template_id}
                            className="p-2 border rounded cursor-pointer hover:bg-muted transition-colors"
                            onClick={() => handleTemplateSelect(template.template_id)}
                            data-testid={`quick-template-${template.template_id}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {getCategoryIcon(template.category)}
                              <span className="text-sm font-medium truncate">{template.name}</span>
                            </div>
                            <div className="flex gap-1">
                              {getChannelBadge(template.channel)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button variant="outline" className="w-full justify-start" onClick={() => setShowAiDialog(true)}>
                      <Sparkles className="w-4 h-4 mr-2 text-purple-500" /> AI Draft Message
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => setShowTranslateDialog(true)}>
                      <Languages className="w-4 h-4 mr-2 text-blue-500" /> Translate Text
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => { setActiveTab("templates"); setShowTemplateForm(true); }}>
                      <Plus className="w-4 h-4 mr-2 text-green-500" /> Create Template
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Message Templates</CardTitle>
                    <CardDescription>Pre-built templates for all communication types</CardDescription>
                  </div>
                  <Button onClick={() => { setEditingTemplate(null); setTemplateForm({ name: "", category: "community", channel: "email", subject: "", body: "", variables: [], is_active: true }); setShowTemplateForm(true); }} data-testid="create-template-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Template
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-4">
                  <Select value={templateFilter.category} onValueChange={(v) => setTemplateFilter({...templateFilter, category: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="financial">Financial</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                      <SelectItem value="drc">DRC</SelectItem>
                      <SelectItem value="community">Community</SelectItem>
                      <SelectItem value="emergency">Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={templateFilter.channel} onValueChange={(v) => setTemplateFilter({...templateFilter, channel: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Channels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Channels</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="in_app">In-App</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {filteredTemplates.map(template => (
                    <Card key={template.template_id} className="border">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div className="p-2 bg-muted rounded-lg">
                              {getCategoryIcon(template.category)}
                            </div>
                            <div>
                              <CardTitle className="text-base">{template.name}</CardTitle>
                              <div className="flex gap-1 mt-1">
                                {getChannelBadge(template.channel)}
                                {getCategoryBadge(template.category)}
                                {template.is_default && <Badge variant="secondary">Default</Badge>}
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => handleTemplateSelect(template.template_id)}>
                                <Send className="w-4 h-4 mr-2" /> Use Template
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditTemplate(template)}>
                                <Edit className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              {!template.is_default && (
                                <DropdownMenuItem onClick={() => handleDeleteTemplate(template.template_id)} className="text-destructive">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {template.subject && (
                          <p className="text-sm text-muted-foreground mb-1"><strong>Subject:</strong> {template.subject}</p>
                        )}
                        <p className="text-sm text-muted-foreground line-clamp-3">{template.body}</p>
                        {template.variables?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {template.variables.slice(0, 5).map(v => (
                              <Badge key={v} variant="outline" className="text-xs">{'{' + v + '}'}</Badge>
                            ))}
                            {template.variables.length > 5 && (
                              <Badge variant="outline" className="text-xs">+{template.variables.length - 5} more</Badge>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Template Form Dialog */}
            <Dialog open={showTemplateForm} onOpenChange={setShowTemplateForm}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
                  <DialogDescription>Create or modify a communication template</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Template Name *</Label>
                      <Input 
                        value={templateForm.name}
                        onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                        placeholder="e.g., Welcome Email"
                        data-testid="template-name-input"
                      />
                    </div>
                    <div>
                      <Label>Channel</Label>
                      <Select value={templateForm.channel} onValueChange={(v) => setTemplateForm({...templateForm, channel: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="sms">SMS</SelectItem>
                          <SelectItem value="in_app">In-App</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={templateForm.category} onValueChange={(v) => setTemplateForm({...templateForm, category: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="financial">Financial</SelectItem>
                          <SelectItem value="compliance">Compliance</SelectItem>
                          <SelectItem value="drc">DRC</SelectItem>
                          <SelectItem value="community">Community</SelectItem>
                          <SelectItem value="lifecycle">Lifecycle</SelectItem>
                          <SelectItem value="emergency">Emergency</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch 
                        checked={templateForm.is_active}
                        onCheckedChange={(v) => setTemplateForm({...templateForm, is_active: v})}
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                  {templateForm.channel === "email" && (
                    <div>
                      <Label>Subject</Label>
                      <Input 
                        value={templateForm.subject}
                        onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                        placeholder="Email subject line..."
                      />
                    </div>
                  )}
                  <div>
                    <Label>Message Body *</Label>
                    <Textarea 
                      value={templateForm.body}
                      onChange={(e) => setTemplateForm({...templateForm, body: e.target.value})}
                      placeholder="Enter template body..."
                      rows={8}
                      data-testid="template-body-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use {'{variable_name}'} for variables. They will be automatically detected.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowTemplateForm(false)}>Cancel</Button>
                  <Button onClick={handleCreateTemplate} data-testid="save-template-btn">
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Messages by Channel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { key: 'email', icon: Mail, color: 'bg-blue-500' },
                        { key: 'sms', icon: MessageSquare, color: 'bg-green-500' },
                        { key: 'in_app', icon: Bell, color: 'bg-purple-500' }
                      ].map(({ key, icon: Icon, color }) => {
                        const count = analytics.by_channel?.[key] || 0;
                        const total = analytics.total_messages || 1;
                        const percent = (count / total * 100).toFixed(0);
                        return (
                          <div key={key}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                <span className="capitalize">{key.replace('_', '-')}</span>
                              </div>
                              <span className="font-bold">{count}</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div className={`h-full ${color}`} style={{ width: `${percent}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Messages by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(analytics.by_category || {}).map(([category, count]) => (
                        <div key={category} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(category)}
                            <span className="capitalize">{category}</span>
                          </div>
                          <span className="font-bold">{count}</span>
                        </div>
                      ))}
                      {Object.keys(analytics.by_category || {}).length === 0 && (
                        <p className="text-muted-foreground text-center py-4">No messages sent yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Delivery Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { status: 'sent', icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
                        { status: 'queued', icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50' },
                        { status: 'delivered', icon: Check, color: 'text-blue-500', bg: 'bg-blue-50' },
                        { status: 'failed', icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' }
                      ].map(({ status, icon: Icon, color, bg }) => (
                        <div key={status} className={`p-3 rounded-lg ${bg}`}>
                          <div className="flex items-center gap-2">
                            <Icon className={`w-5 h-5 ${color}`} />
                            <div>
                              <p className="text-xl font-bold">{analytics.by_status?.[status] || 0}</p>
                              <p className="text-xs text-muted-foreground capitalize">{status}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2 lg:col-span-3">
                  <CardHeader>
                    <CardTitle>Top Used Templates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.top_templates?.length > 0 ? (
                      <div className="space-y-2">
                        {analytics.top_templates.map((item, idx) => {
                          const template = templates.find(t => t.template_id === item._id);
                          return (
                            <div key={item._id} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center gap-3">
                                <span className="text-lg font-bold text-muted-foreground">#{idx + 1}</span>
                                <div>
                                  <p className="font-medium">{template?.name || item._id}</p>
                                  <p className="text-xs text-muted-foreground">{template?.category}</p>
                                </div>
                              </div>
                              <Badge>{item.count} uses</Badge>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No template usage data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* In-App Messages Tab */}
          <TabsContent value="in-app">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>In-App Messages</CardTitle>
                    <CardDescription>Notifications and announcements within the portal</CardDescription>
                  </div>
                  <Button variant="outline">
                    <Plus className="w-4 h-4 mr-2" /> Create Announcement
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {inAppMessages.length > 0 ? (
                  <div className="space-y-3">
                    {inAppMessages.map(msg => (
                      <div key={msg.message_id} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Bell className="w-4 h-4" />
                              <span className="font-medium">{msg.title}</span>
                              {!msg.is_read && <Badge className="bg-blue-500">New</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">{msg.body}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {msg.owner_id ? `To: ${msg.owner_id}` : 'Broadcast'} • {new Date(msg.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant={msg.priority === 'urgent' ? 'destructive' : 'secondary'}>
                            {msg.priority}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No in-app messages yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );

  if (embedded) return pageContent;
  return <AdminSidebar>{pageContent}</AdminSidebar>;
};

export default CommunicationsHubPage;
